import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { FastlyImage } from '@components/common/image'

type Step = {
  letter: string
  title: string
  subtitle: string
  copy: string
}

const STEPS: Step[] = [
  {
    letter: 'L',
    title: 'Learn',
    subtitle: 'See it in concept',
    copy:
      "Your child's tutor never opens with a formula. They open with a visual, something your child can see and make sense of, so the concept feels real before any rule is introduced.",
  },
  {
    letter: 'P',
    title: 'Practice',
    subtitle: 'Solve it together',
    copy:
      "The tutor doesn't solve it for your child. They solve it with them, on the same board, working through it together until it clicks.",
  },
  {
    letter: 'A',
    title: 'Apply',
    subtitle: 'Do it independently',
    copy:
      'Then your child tries it alone. The tutor is still there, watching, but they step back and let your child work through it without help.',
  },
  {
    letter: 'A',
    title: 'Assess',
    subtitle: "Check while it's fresh",
    copy:
      'A quick check-in right after - not a test, just a way to make sure the concept has landed before the class moves on.',
  },
  {
    letter: 'R',
    title: 'Reinforce',
    subtitle: 'Make it permanent',
    copy:
      'A worksheet goes home with your child at exactly the right difficulty level, so the concept stays with them long after the class ends.',
  },
]

type Props = {
  urlQuery?: string
}

const LpaarMethod = ({ urlQuery }: Props) => {
  return (
    <section className="bg-blue-50 bg-opacity-25 py-12 lg:py-16 px-5">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <h2 className="text-2xl lg:text-4xl font-600 leading-tight mb-3 text-grey max-w-4xl mx-auto">
            The Codingal LPAAR Method
            <sup className="text-base lg:text-lg ml-1">tm</sup>
          </h2>
          <p className="text-xl lg:text-2xl font-600 text-orange mb-4 max-w-4xl mx-auto">
            Built on STEM, Bloom&apos;s Taxonomy, BIDE, and the Goldilocks
            Principle.
          </p>
          <p className="text-base lg:text-lg text-grey max-w-4xl mx-auto">
            Every Codingal Math class follows a five-step structure grounded in
            how children actually build understanding, rather than simply
            receiving information and being expected to retain it.
          </p>
        </div>

        <div className="flex flex-col">
          {STEPS.map((step, i) => {
            const isLast = i === STEPS.length - 1
            return (
              <div key={i} className="flex items-stretch gap-2 sm:gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange border border-white text-white flex items-center justify-center font-700 text-lg sm:text-xl shadow-lg">
                    {step.letter}
                  </div>
                  {!isLast && <div className="w-px flex-grow bg-orange" />}
                </div>
                <div className={`flex-1 ${!isLast ? 'pb-6' : ''}`}>
                  <div className="bg-white rounded-xl px-2 lg:px-3 py-1 lg:py-2">
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 mb-2">
                      <h3 className="text-lg lg:text-xl font-700 text-grey-900">
                        {step.title}
                      </h3>
                      <p className="lg:text-lg font-600 text-green-200 uppercase">
                        {step.subtitle}
                      </p>
                    </div>
                    <p className="text-grey-800 leading-snug">{step.copy}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="bg-orange-100 bg-opacity-40 border border-orange-200 rounded-xl p-3 lg:p-4 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 mt-10">
          <FastlyImage
            src="/images/courses/math/icons/cody-with-pencil-circular.png"
            alt="Cody"
            width={60}
            height={60}
            className="flex-shrink-0"
          />
          <p className="lg:text-lg text-orange leading-snug text-center sm:text-left">
            Every class is pitched at exactly the right level for your child,
            challenging enough to stretch them but never so difficult that they
            switch off. This is the Goldilocks Principle, and it&apos;s the
            reason Codingal students stay engaged long enough for Math to
            actually click.
          </p>
        </div>

        <div className="flex justify-center mt-8">
          <BookFreeClassBtn query={urlQuery} />
        </div>
      </div>
    </section>
  )
}

export default LpaarMethod
